import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import GuestLayout from "../../Layouts/GuestLayout";
import {
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SecondaryButton from "../../Components/SecondaryButton";
import GroupedContainer from "../../Components/GroupedContainer";

export default function CarrierRegister() {
  const [formData, setFormData] = useState({
    usdot: '',
    companyName: '',
    streetAddress: '',
    zipcode: '',
    city: '',
    stateValue: '',
    country: '',
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    trailerType: 'Open',
    isAllowedToOperate: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // "error" ou "success"
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signature, setSignature] = useState(null);
  const [agreeMessages, setAgreeMessages] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [usdotSearched, setUsdotSearched] = useState(false);
  const [fmcsaLockedFields, setFmcsaLockedFields] = useState({
    companyName: false,
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Referências para a assinatura
  const canvasRef = useRef(null);
  const signatureTimeoutRef = useRef(null);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const parts = [];

    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length > 3) parts.push(digits.slice(3, 6));
    if (digits.length > 6) parts.push(digits.slice(6, 10));

    return parts.join("-");
  };

  const splitCompanyOfficerName = (companyOfficerName) => {
    const nameParts = String(companyOfficerName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (nameParts.length === 0) {
      return { firstName: "", lastName: "" };
    }

    return {
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(" "),
    };
  };

  const handleFieldChange = (field) => (e) => {
    let value = e.target.value;

    if (field === "phone") {
      value = formatPhoneNumber(value);
    }

    setFormData({ ...formData, [field]: value });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Função para verificar se o canvas tem assinatura
  const hasCanvasSignature = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    for (let i = 0; i < imageData.length; i += 4) {
      if (imageData[i + 3] !== 0) { // Verifica canal alpha (transparência)
        return true;
      }
    }
    return false;
  };

  // Função para salvar a assinatura automaticamente COMO IMAGEM
  const saveSignatureAutomatically = () => {
    const canvas = canvasRef.current;

    if (!canvas || !hasCanvasSignature(canvas)) {
      setSignature(null);
      setHasSignature(false);
      return;
    }

    // Converter canvas para blob (PNG)
    canvas.toBlob((blob) => {
      if (blob) {
        // Criar arquivo de imagem PNG
        const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });
        setSignature(signatureFile);
        setHasSignature(true);
      }
    }, 'image/png');
  };

  // Função para limpar a assinatura
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
    setHasSignature(false);
  };

  // Inicializar canvas de assinatura
  const initSignaturePad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Configurações do canvas
    canvas.width = canvas.offsetWidth;
    canvas.height = 200;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Limpar canvas inicialmente
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;

    const startDrawing = (e) => {
      isDrawing = true;
      const rect = canvas.getBoundingClientRect();
      lastX = e.clientX - rect.left;
      lastY = e.clientY - rect.top;
    };

    const draw = (e) => {
      if (!isDrawing) return;

      const rect = canvas.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();

      lastX = currentX;
      lastY = currentY;
    };

    const stopDrawing = () => {
      isDrawing = false;

      // Salvar assinatura automaticamente após um pequeno delay
      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }

      signatureTimeoutRef.current = setTimeout(() => {
        saveSignatureAutomatically();
      }, 500); // 500ms delay para garantir que o usuário terminou de desenhar
    };

    // Event listeners para desktop
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    // Event listeners para mobile
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const mouseEvent = new MouseEvent('mouseup', {});
      canvas.dispatchEvent(mouseEvent);
    });

    // Cleanup function
    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseout', stopDrawing);
      canvas.removeEventListener('touchstart', () => { });
      canvas.removeEventListener('touchmove', () => { });
      canvas.removeEventListener('touchend', () => { });

      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }
    };
  };

  // Efeito para inicializar a assinatura quando o componente montar
  useEffect(() => {
    const cleanup = initSignaturePad();
    return cleanup;
  }, []);

  const validateFields = () => {
    const errs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

    if (!formData.usdot) errs.usdot = "USDOT is required";
    if (!formData.companyName) errs.companyName = "Company name is required";
    if (!formData.streetAddress) errs.streetAddress = "Street address is required";
    if (!formData.zipcode) errs.zipcode = "Zipcode is required";
    if (!formData.city) errs.city = "City is required";
    if (!formData.stateValue) errs.stateValue = "State is required";
    if (!formData.country) errs.country = "Country is required";
    if (!formData.firstName) errs.firstName = "First name is required";
    if (!formData.lastName) errs.lastName = "Last name is required";
    if (!formData.username) errs.username = "Username is required";

    if (!formData.email) {
      errs.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errs.email = "Invalid email format";
    }

    if (!formData.phone) {
      errs.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      errs.phone = "Phone must be in the format 999-999-9999";
    }

    if (!formData.password) {
      errs.password = "Password is required";
    } else {
      if (formData.password.length < 6) {
        errs.password = "Password must be at least 6 characters";
      } else {
        const hasLetter = /[A-Za-z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);

        if (!hasLetter || !hasNumber) {
          errs.password = "Password must contain at least one letter and one number";
        }
      }
    }

    if (!formData.confirmPassword) {
      errs.confirmPassword = "Confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    if (!formData.trailerType) errs.trailerType = "Trailer type is required";
    if (!formData.isAllowedToOperate) errs.isAllowedToOperate = "Operation status is required";

    // Validar checkboxes
    if (!agreeMessages) errs.agreeMessages = "You must agree to receive messages";
    if (!agreeTerms) errs.agreeTerms = "You must agree to the terms of service and privacy policy";

    // Validar assinatura
    if (!signature) {
      errs.signature = "Signature is required";
    }

    return errs;
  };

  const clearForm = () => {
    setFormData({
      usdot: '',
      companyName: '',
      streetAddress: '',
      zipcode: '',
      city: '',
      stateValue: '',
      country: '',
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      trailerType: 'Open',
      isAllowedToOperate: '',
    });
    setUsdotSearched(false);
    setFmcsaLockedFields({
      companyName: false,
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
    });
    setSignature(null);
    setHasSignature(false);
    setAgreeMessages(false);
    setAgreeTerms(false);
    clearSignature();
  };

  const fetchUsdotData = async () => {
    if (!formData.usdot) {
      setSnackbarMessage("USDOT is required");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    setUsdotSearched(false);
    setFormData(prev => ({
      ...prev,
      companyName: '',
      streetAddress: '',
      zipcode: '',
      city: '',
      stateValue: '',
      country: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isAllowedToOperate: '',
    }));
    setFmcsaLockedFields({
      companyName: false,
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
    });

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_PREFIX}/dot_info/${formData.usdot}`);
      const carrier = response.data?.content?.carrier;

      if (carrier) {
        const ownerName = splitCompanyOfficerName(carrier.companyOfficer1);
        const companyName = String(carrier.legalName || "").trim();
        const email = String(carrier.emailAddress || "").trim();
        const phone = formatPhoneNumber(String(carrier.phone || ""));

        setFormData(prev => ({
          ...prev,
          companyName,
          streetAddress: carrier.phyStreet || '',
          city: carrier.phyCity || '',
          stateValue: carrier.phyState || '',
          country: carrier.phyCountry || '',
          zipcode: carrier.phyZipcode || '',
          firstName: ownerName.firstName,
          lastName: ownerName.lastName,
          email,
          phone,
          isAllowedToOperate: carrier.allowedToOperate || '',
        }));

        setFmcsaLockedFields({
          companyName: Boolean(companyName),
          firstName: Boolean(ownerName.firstName),
          lastName: Boolean(ownerName.lastName),
          email: Boolean(email),
          phone: Boolean(phone),
        });
        setUsdotSearched(true);
        setSnackbarMessage("USDOT data fetched successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("No carrier data found for this USDOT.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(
        err.response?.data?.message ||
        "FMCSA services are temporarily unavailable. Please try again in a few moments."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSnackbarMessage("");

    // Verificar se há assinatura antes de enviar
    if (!signature) {
      const canvas = canvasRef.current;
      if (canvas && hasCanvasSignature(canvas)) {
        saveSignatureAutomatically();
        // Aguardar um pouco para a assinatura ser salva
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        setErrors(prev => ({ ...prev, signature: "Signature is required" }));
        setSnackbarMessage("Please provide a signature.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    }

    // Se ainda não tem assinatura após tentar salvar
    if (!signature) {
      setErrors(prev => ({ ...prev, signature: "Signature is required" }));
      setSnackbarMessage("Please provide a signature.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setSnackbarMessage(Object.values(fieldErrors)[0]);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      const partnerCompanyId = localStorage.getItem('companyId') || 1;
      const submitData = new FormData();

      submitData.append('dot', formData.usdot);
      submitData.append('partner_company_name', formData.companyName);
      submitData.append('address', formData.streetAddress);
      submitData.append('city', formData.city);
      submitData.append('state', formData.stateValue);
      submitData.append('zipcode', formData.zipcode);
      submitData.append('country', formData.country);
      submitData.append('is_allowed_to_operate', formData.isAllowedToOperate === 'Y' ? '1' : '0');
      submitData.append('trailer_type', formData.trailerType);
      submitData.append('signature', signature); // Agora é uma imagem PNG
      submitData.append('first_name', formData.firstName);
      submitData.append('last_name', formData.lastName);
      submitData.append('username', formData.username);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('password', formData.password);
      submitData.append('c_password', formData.confirmPassword);

      await axios.post(
        'https://new.cajuu.app/api/user-carrier',
        submitData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      localStorage.setItem("registrationSuccess", "true");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userRoleId");

      clearForm();
      window.location.href = "/login";
    } catch (err) {
      // PARAR LOADING IMEDIATAMENTE EM CASO DE ERRO
      setIsLoading(false);

      const errorData = err.response?.data;
      const validationData = errorData?.data;
      const emailError = Array.isArray(validationData?.email)
        ? validationData.email[0]
        : validationData?.email;

      if (emailError) {
        setErrors(prev => ({ ...prev, email: emailError }));
        setSnackbarMessage(emailError);
      } else if (validationData && typeof validationData === 'object') {
        const firstKey = Object.keys(validationData)[0];
        const firstError = validationData[firstKey];
        const firstMessage = Array.isArray(firstError) ? firstError[0] : firstError;

        if (firstKey) {
          const frontendFieldMap = {
            partner_company_name: 'companyName',
            first_name: 'firstName',
            last_name: 'lastName',
            c_password: 'confirmPassword',
            trailer_type: 'trailerType',
            is_allowed_to_operate: 'isAllowedToOperate',
            dot: 'usdot',
            state: 'stateValue',
            address: 'streetAddress',
          };
          const frontendField = frontendFieldMap[firstKey] || firstKey;
          setErrors(prev => ({ ...prev, [frontendField]: firstMessage }));
        }

        setSnackbarMessage(firstMessage || "Registration failed.");
      } else if (typeof errorData?.message === 'string') {
        setSnackbarMessage(`Registration failed: ${errorData.message}`);
      } else if (typeof errorData?.message === 'object') {
        const firstKey = Object.keys(errorData.message)[0];
        const firstMessage = errorData.message[firstKey];
        setSnackbarMessage(`Registration failed: ${firstMessage}`);
      } else {
        setSnackbarMessage("An unknown error occurred.");
      }

      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Função para obter o texto e cor do campo Is Allowed To Operate
  const getAllowedToOperateDisplay = () => {
    if (formData.isAllowedToOperate === 'Y') {
      return { text: 'Yes', color: '#10b981' }; // Verde
    } else if (formData.isAllowedToOperate === 'N') {
      return { text: 'No', color: '#ef4444' }; // Vermelho
    }
    return { text: '', color: '#ffffff' };
  };

  const allowedToOperateDisplay = getAllowedToOperateDisplay();

  return (
    <GuestLayout title="Carrier Register">
      <div className="w-full mt-4 sm:mt-5 flex justify-center items-center">
        <div className="flex text-xl sm:text-2xl md:text-3xl tracking-tight text-amber-500 font-semibold">
          Sign up to
          <div className="text-white flex items-center">
            <img src="/img/cajuu.png" alt="Cajuu Logo" className="w-7 h-7 sm:w-9 sm:h-9 ml-2" />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      <div className="mt-7">
        <TextField
          label="USDOT"
          required
          color="secondary"
          value={formData.usdot}
          onChange={handleFieldChange("usdot")}
          error={!!errors.usdot}
          helperText={errors.usdot}
          className="w-full"
        />
        <button
          onClick={fetchUsdotData}
          disabled={isLoading}
          className={`bg-amber-500 rounded-lg font-semibold hover:bg-amber-600 hover:text-white border-none h-12 text-white w-full justify-center mt-4 transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Seeking out...
            </span>
          ) : (
            "Search USDOT"
          )}
        </button>
      </div>

      {/* Company Name e Street Address - só aparecem após busca do USDOT */}
      {usdotSearched && (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-7">
          <TextField
            label="Company Name"

            color="secondary"
            value={formData.companyName}
            onChange={handleFieldChange("companyName")}
            error={!!errors.companyName}
            helperText={errors.companyName}
            className="w-full"
            InputProps={{
              readOnly: fmcsaLockedFields.companyName,
            }}
            sx={{
              '& .MuiInputBase-input.Mui-readOnly': {

                cursor: 'not-allowed',
              },
            }}
          />
          <TextField
            label="Street Address"

            color="secondary"
            value={formData.streetAddress}
            onChange={handleFieldChange("streetAddress")}
            error={!!errors.streetAddress}
            helperText={errors.streetAddress}
            className="w-full"
            InputProps={{
              readOnly: true,
            }}
            sx={{
              '& .MuiInputBase-input.Mui-readOnly': {

                cursor: 'not-allowed',
              },
            }}
          />
        </div>
      )}

      {/* Campos de endereço - só aparecem após busca do USDOT */}
      {usdotSearched && (
        <GroupedContainer className="w-full mt-6 sm:mt-8">
          <div className="grid grid-cols-1 w-full sm:grid-cols-2 gap-4 sm:gap-6">
            <TextField
              label="Zipcode"

              color="secondary"
              value={formData.zipcode}
              onChange={handleFieldChange("zipcode")}
              error={!!errors.zipcode}
              helperText={errors.zipcode}
              className="w-full"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-input.Mui-readOnly': {

                  cursor: 'not-allowed',
                },
              }}
            />
            <TextField
              label="City"

              color="secondary"
              value={formData.city}
              onChange={handleFieldChange("city")}
              error={!!errors.city}
              helperText={errors.city}
              className="w-full"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-input.Mui-readOnly': {

                  cursor: 'not-allowed',
                },
              }}
            />
            <TextField
              label="State"

              color="secondary"
              value={formData.stateValue}
              onChange={handleFieldChange("stateValue")}
              error={!!errors.stateValue}
              helperText={errors.stateValue}
              className="w-full"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-input.Mui-readOnly': {

                  cursor: 'not-allowed',
                },
              }}
            />
            <TextField
              label="Country"

              color="secondary"
              value={formData.country}
              onChange={handleFieldChange("country")}
              error={!!errors.country}
              helperText={errors.country}
              className="w-full"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-input.Mui-readOnly': {

                  cursor: 'not-allowed',
                },
              }}
            />
            {/* Is Allowed To Operate Display - Campo somente leitura com cores */}
            <div className="sm:col-span-2">
              <TextField
                label="Is Allowed To Operate"
                color="secondary"
                value={allowedToOperateDisplay.text}
                className="w-full"
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-readOnly': {
                    cursor: 'not-allowed',
                    color: allowedToOperateDisplay.color,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ffffff',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#6b7280',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9ca3af',
                    },
                  },
                }}
              />
            </div>
          </div>
        </GroupedContainer>
      )}

      <GroupedContainer className="w-full mt-6 sm:mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 w-full gap-4 sm:gap-6 items-center">
          <TextField
            label="First Name"
            required
            color="secondary"
            value={formData.firstName}
            onChange={handleFieldChange("firstName")}
            error={!!errors.firstName}
            helperText={errors.firstName}
            className="w-full"
            InputProps={{
              readOnly: fmcsaLockedFields.firstName,
            }}
            sx={{
              '& .MuiInputBase-input.Mui-readOnly': {
                cursor: 'not-allowed',
              },
            }}
          />
          <TextField
            label="Last Name"
            required
            color="secondary"
            value={formData.lastName}
            onChange={handleFieldChange("lastName")}
            error={!!errors.lastName}
            helperText={errors.lastName}
            className="w-full"
            InputProps={{
              readOnly: fmcsaLockedFields.lastName,
            }}
            sx={{
              '& .MuiInputBase-input.Mui-readOnly': {
                cursor: 'not-allowed',
              },
            }}
          />
        </div>
      </GroupedContainer>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-7">
        <TextField
          label="Username"
          required
          color="secondary"
          value={formData.username}
          onChange={handleFieldChange("username")}
          error={!!errors.username}
          helperText={errors.username}
          className="w-full"
        />
        <TextField
          label="Email"
          required
          color="secondary"
          value={formData.email}
          onChange={handleFieldChange("email")}
          error={!!errors.email}
          helperText={errors.email}
          className="w-full"
          InputProps={{
            readOnly: fmcsaLockedFields.email,
          }}
          sx={{
            '& .MuiInputBase-input.Mui-readOnly': {
              cursor: 'not-allowed',
            },
          }}
        />
        <TextField
          label="Phone"
          required
          color="secondary"
          value={formData.phone}
          onChange={handleFieldChange("phone")}
          error={!!errors.phone}
          helperText={errors.phone}
          className="w-full"
          InputProps={{
            readOnly: fmcsaLockedFields.phone,
          }}
          sx={{
            '& .MuiInputBase-input.Mui-readOnly': {
              cursor: 'not-allowed',
            },
          }}
        />

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          required
          color="secondary"
          value={formData.password}
          onChange={handleFieldChange("password")}
          error={!!errors.password}
          helperText={errors.password}
          className="w-full"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff className="text-white" /> : <Visibility className="text-white" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          required
          color="secondary"
          value={formData.confirmPassword}
          onChange={handleFieldChange("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          className="w-full"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                  {showConfirmPassword ? <VisibilityOff className="text-white" /> : <Visibility className="text-white" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Trailer Type Select */}
        <FormControl fullWidth color="secondary" error={!!errors.trailerType}>
          <InputLabel id="trailer-type-label">Trailer Type *</InputLabel>
          <Select
            labelId="trailer-type-label"
            value={formData.trailerType}
            label="Trailer Type *"
            onChange={handleSelectChange("trailerType")}
            className="w-full"
          >
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="Enclosed">Enclosed</MenuItem>
          </Select>
          {errors.trailerType && (
            <p className="text-red-500 text-xs mt-1">{errors.trailerType}</p>
          )}
        </FormControl>

        {/* Checkboxes */}
        <div className="space-y-4 mt-4">
          {/* Messages Checkbox */}
          <FormControl error={!!errors.agreeMessages} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeMessages}
                  onChange={(e) => setAgreeMessages(e.target.checked)}
                  color="secondary"
                  sx={{
                    color: errors.agreeMessages ? '#ef4444' : '#ffffff',
                    '&.Mui-checked': {
                      color: '#f59e0b',
                    },
                  }}
                />
              }
              label={
                <span className="text-white text-sm">
                  I agree to receive text and e-mail messages from Cajuu including offers and promotions.
                </span>
              }
              className="w-full"
            />
            {errors.agreeMessages && (
              <p className="text-red-500 text-xs mt-1 ml-9">{errors.agreeMessages}</p>
            )}
          </FormControl>

          {/* Terms Checkbox */}
          <FormControl error={!!errors.agreeTerms} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  color="secondary"
                  sx={{
                    color: errors.agreeTerms ? '#ef4444' : '#ffffff',
                    '&.Mui-checked': {
                      color: '#f59e0b',
                    },
                  }}
                />
              }
              label={
                <span className="text-white text-sm">
                  I agree to the{' '}
                  <a
                    href="https://cajuu.app/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-500 hover:text-amber-400 underline transition-colors duration-200"
                  >
                    terms of service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://cajuu.app/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-500 hover:text-amber-400 underline transition-colors duration-200"
                  >
                    privacy policy
                  </a>{' '}
                </span>
              }
              className="w-full"
            />
            {errors.agreeTerms && (
              <p className="text-red-500 text-xs mt-1 ml-9">{errors.agreeTerms}</p>
            )}
          </FormControl>
        </div>

        {/* Digital Signature - SALVA AUTOMATICAMENTE */}
        <div className="mt-2">
          <label className="block text-sm font-medium text-white mb-2">
            Customer Signature *
          </label>
          <div className="border-2 border-gray-600 rounded-lg bg-white mb-4 w-full">
            <canvas
              ref={canvasRef}
              className="w-full h-48 cursor-crosshair"
              style={{ touchAction: 'none' }}
            />
          </div>
          <p className="text-white text-sm mb-4">
            Draw your signature in the box above - It will be saved automatically
          </p>

          <div className="flex justify-end mt-2">
            <SecondaryButton
              onClick={clearSignature}
              className="text-gray-400 hover:text-white text-sm"
            >
              Clear Signature
            </SecondaryButton>
          </div>

          {hasSignature && (
            <div className="text-green-500 text-sm mt-2">
              ✓ Signature saved automatically
            </div>
          )}

          {errors.signature && (
            <p className="text-red-500 text-xs mt-1">{errors.signature}</p>
          )}
        </div>
      </div>

      <div className="mt-6 sm:mt-10">
        <SecondaryButton
          onClick={handleSubmit}
          disabled={isLoading}
          className={`bg-amber-500 hover:bg-amber-600 hover:text-white border-none h-12 text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Submitting...
            </span>
          ) : (
            "Submit"
          )}
        </SecondaryButton>

        <div className="flex w-full justify-center sm:flex-row items-center mt-6 sm:mt-8">
          <a
            href="/login"
            className="underline underline-offset-4 hover:decoration-4 text-sm font-medium text-white hover:text-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary mb-2 sm:mb-0"
          >
            Already have an account?
          </a>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "500px",
            backgroundColor: snackbarSeverity === "success" ? "#16a34a" : "#dc2626", // Verde para sucesso, vermelho para erro
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </GuestLayout>
  );
}